/**
 * Application initialization and configuration
 * Sets up logging, performance monitoring, and error handling
 */

import logger from './utils/logger';
import performance from './utils/performance';

/**
 * Initialize the app with production-ready configuration
 */
export const initializeApp = () => {
  // Configure logging based on environment
  logger.configureLogger({
    level: process.env.NODE_ENV === 'production' ? logger.LOG_LEVELS.ERROR : logger.LOG_LEVELS.DEBUG,
    useColors: process.env.NODE_ENV !== 'production',
    captureStack: process.env.NODE_ENV !== 'production',
    externalLogger: process.env.NODE_ENV === 'production' ? logToExternalService : null
  });

  // Configure performance monitoring
  performance.configure({
    enabled: true,
    sampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1,
    reportingEndpoint: process.env.REACT_APP_METRICS_ENDPOINT || null,
    debugMode: process.env.NODE_ENV !== 'production'
  });

  // Initialize performance measurements
  performance.initLoadMeasurement();

  // Set up global error handler for uncaught errors
  window.addEventListener('error', (event) => {
    logger.error('Uncaught error', { 
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      stack: event.error?.stack
    });
    
    // Allow the error to continue to browser's default handler
    return false;
  });

  // Set up unhandled promise rejection handler
  window.addEventListener('unhandledrejection', (event) => {
    logger.error('Unhandled promise rejection', {
      reason: event.reason?.message || event.reason,
      stack: event.reason?.stack
    });
    
    // Allow the rejection to continue to browser's default handler
    return false;
  });

  // Check for browser features that we depend on
  checkBrowserCompatibility();

  // Log app initialization
  logger.info('Application initialized', {
    version: process.env.REACT_APP_VERSION || '1.0.0',
    environment: process.env.NODE_ENV,
    buildTime: process.env.REACT_APP_BUILD_TIME
  });
};

/**
 * Log to external service (placeholder)
 * Replace with actual implementation when using a real service
 * @param {Object} logData - Log data
 */
const logToExternalService = (logData) => {
  // In a real app, this would send to a service like Sentry, LogRocket, etc.
  const endpoint = process.env.REACT_APP_LOGGING_ENDPOINT;
  if (!endpoint) return;

  // Don't block the UI thread for logging
  setTimeout(() => {
    try {
      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(logData),
        keepalive: true
      }).catch(err => console.error('Failed to send log to external service', err));
    } catch (error) {
      console.error('Failed to send log to external service', error);
    }
  }, 0);
};

/**
 * Check for required browser features
 * Logs warnings if features are missing
 */
const checkBrowserCompatibility = () => {
  const compatibility = {
    canvas: !!document.createElement('canvas').getContext,
    localStorage: !!window.localStorage,
    webGL: (function() {
      try {
        const canvas = document.createElement('canvas');
        return !!(window.WebGLRenderingContext && 
                (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')));
      } catch (e) {
        return false;
      }
    })(),
    touchEvents: 'ontouchstart' in window,
    clipboardAPI: navigator.clipboard && !!navigator.clipboard.writeText,
    shareAPI: navigator.share && !!navigator.canShare
  };

  // Log compatibility information
  logger.info('Browser compatibility check', compatibility);

  // Warn about missing critical features
  if (!compatibility.canvas) {
    logger.warn('Browser does not support Canvas - TacticsBoard requires this feature');
  }

  if (!compatibility.localStorage) {
    logger.warn('Browser does not support localStorage - Saving and loading will not work');
  }

  // Return compatibility object for app use
  return compatibility;
};

export default initializeApp;
